// src/components/device/DeviceControlPage.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Button,
  Tabs,
  Tab,
  Divider,
  Tooltip,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TerminalIcon from "@mui/icons-material/Terminal";
import RefreshIcon from "@mui/icons-material/Refresh";
import SpeedIcon from "@mui/icons-material/Speed";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SensorsIcon from "@mui/icons-material/Sensors";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import NumbersIcon from "@mui/icons-material/Numbers";

import DeviceOverview from "./DeviceOverview";
import DeviceCommands from "./DeviceCommands";
import DeviceCommandHistory from "./DeviceCommandHistory";
import DeviceLiveData from "./device/DeviceLiveData";
import DevicePermissions from "./DevicePermissions";

function a11yProps(index) {
  return { id: `dc-tab-${index}`, "aria-controls": `dc-tabpanel-${index}` };
}

function StatusChip({ status }) {
  const s = (status || "unknown").toLowerCase();
  const color =
    s === "online" ? "success" :
    s === "offline" ? "error" :
    s === "sleep" ? "warning" :
    "default";

  return <Chip size="small" label={s.toUpperCase()} color={color} variant={s === "unknown" ? "outlined" : "filled"} />;
}

export default function DeviceControlPage({ device, user, assetsData, onRefresh }) {
  const [tab, setTab] = useState(0);

  const live = device?.liveData || {};
  const lastSeen = device?.lastDataReceivedAt ? new Date(device.lastDataReceivedAt).toLocaleString() : "-";
  const speed = useMemo(() => Number(live?.speed || 0), [live]);

  // Backend now owns command authorization checks; frontend keeps only auth guard.
  const role = String(user?.role || "").toLowerCase();
  const canSendCommands = !!user;

  const canSeePermissionsTab = role === "admin";

  const canSendRaw = canSendCommands;

  const tabs = useMemo(() => {
    const base = [
      { label: "Overview" },
      ...(canSendCommands ? [{ label: "Commands" }] : []),
      { label: "History" },
      { label: "Live" },
      ...(canSeePermissionsTab ? [{ label: "Permissions" }] : []),
    ];
    return base;
  }, [canSendCommands, canSeePermissionsTab]);

  const currentTabKey = useMemo(() => {
    // map index to view key depending on hidden tabs
    const keys = [];
    keys.push("overview");
    if (canSendCommands) keys.push("commands");
    keys.push("history");
    keys.push("live");
    if (canSeePermissionsTab) keys.push("permissions");
    return keys[tab] || "overview";
  }, [tab, canSendCommands, canSeePermissionsTab]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Header */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Stack spacing={0.5}>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                  {device?.name || "Device"} • Control Center
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Commands, live status, history and permissions — all in one place.
                </Typography>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                  <StatusChip status={device?.status} />

                  <Chip
                    size="small"
                    icon={<NumbersIcon />}
                    label={`IMEI: ${device?.imei || "-"}`}
                    variant="outlined"
                  />

                  <Chip
                    size="small"
                    icon={<PersonIcon />}
                    label={`Assigned: ${device?.assignedToUser?.username || "Unassigned"}`}
                    variant="outlined"
                  />

                  <Chip
                    size="small"
                    icon={<LockIcon />}
                    color={canSendCommands ? "success" : "default"}
                    label={`Command Access: ${canSendCommands ? "Allowed" : "Blocked"}`}
                    variant={canSendCommands ? "filled" : "outlined"}
                  />
                </Stack>

                {!canSendCommands && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Sign in to use command controls.
                  </Alert>
                )}
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-start", md: "flex-end" }} flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => window.history.back()}
                >
                  Back
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={onRefresh}
                >
                  Refresh
                </Button>

                {canSendCommands && (
                  <Button
                    variant="contained"
                    startIcon={<TerminalIcon />}
                    onClick={() => {
                      // switch to "Commands" tab if present
                      const idx = tabs.findIndex(t => t.label === "Commands");
                      if (idx >= 0) setTab(idx);
                    }}
                  >
                    Commands
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Card sx={{ borderRadius: 3, height: "100%" }}>
            <CardContent>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <SensorsIcon fontSize="small" />
                  <Typography variant="caption" color="text.secondary">Vehicle</Typography>
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {live?.vehicleStatus || "-"}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Card sx={{ borderRadius: 3, height: "100%" }}>
            <CardContent>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LockIcon fontSize="small" />
                  <Typography variant="caption" color="text.secondary">ACC</Typography>
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {live?.accStatus ? "ON" : "OFF"}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Card sx={{ borderRadius: 3, height: "100%" }}>
            <CardContent>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <SpeedIcon fontSize="small" />
                  <Typography variant="caption" color="text.secondary">Speed</Typography>
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {speed} km/h
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Card sx={{ borderRadius: 3, height: "100%" }}>
            <CardContent>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TerminalIcon fontSize="small" />
                  <Typography variant="caption" color="text.secondary">Model</Typography>
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {device?.model || "-"}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Card sx={{ borderRadius: 3, height: "100%" }}>
            <CardContent>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <AccessTimeIcon fontSize="small" />
                  <Typography variant="caption" color="text.secondary">Last Seen</Typography>
                </Stack>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {lastSeen}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Card sx={{ borderRadius: 3, height: "100%" }}>
            <CardContent>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LockIcon fontSize="small" />
                  <Typography variant="caption" color="text.secondary">Command</Typography>
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {canSendCommands ? "Allowed" : "Blocked"}
                </Typography>
                {canSendCommands && (
                  <Typography variant="caption" color="text.secondary">
                    Raw: {canSendRaw ? "Allowed" : "Blocked"}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card sx={{ borderRadius: 3 }}>
        <Box sx={{ px: 2, pt: 1 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((t, idx) => (
              <Tab key={t.label} label={t.label} {...a11yProps(idx)} />
            ))}
          </Tabs>
        </Box>
        <Divider />

        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
          {currentTabKey === "overview" && <DeviceOverview device={device} />}
          {currentTabKey === "commands" && (
            <DeviceCommands
              device={device}
              user={user}
              assetsData={assetsData || []}
              canSendCommands={canSendCommands}
              canSendRaw={canSendRaw}
            />
          )}
          {currentTabKey === "history" && <DeviceCommandHistory deviceId={device?._id} imei={device?.imei} />}
          {currentTabKey === "live" && <DeviceLiveData deviceId={device?._id} />}
          {currentTabKey === "permissions" && canSeePermissionsTab && (
            <DevicePermissions device={device} />
          )}
        </Box>
      </Card>
    </Box>
  );
}
